/*
 * TrafficTrendChart — self-contained chart with marketing event overlay.
 *
 * Renders the Traffic Trend line chart (Visitors · Page Views · Signups
 * on the left Y-axis, Sales $ on the right) with:
 *   - Diamond ReferenceDots for admin-created System events + Affiliate events
 *   - Per-series checkbox legend to toggle any line/event source
 *   - "+ Add event" CTA → modal to create/edit/delete marketing events
 *   - Click any diamond → view / edit / affiliate-network drilldown
 *   - "Filter by Affiliate" chip that dims the chart to a single affiliate
 *
 * The chart is designed to be dropped onto multiple admin pages
 * (/admin/analytics + /admin/sales) without behavioural drift.
 *
 * Props:
 *   timeline       — { timeline: [{date, views, visitors, signups}] } from
 *                     /analytics/command/timeline
 *   salesTimeline  — { timeline: [{date, sales_usd}] } from
 *                     /analytics/sales/timeline (optional; missing = no $ line)
 */
import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceDot,
} from "recharts";
import { Activity, Plus, Trash2, Diamond, DollarSign, Network } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const GOLD = "#C5A55A";
const GOLD_DIM = "#8B7355";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(local) { return local ? new Date(local).toISOString() : null; }

const AnalyticsDiamondMarker = ({ cx, cy, marker, onOpen, color = "#7dd3fc" }) => (
  <g
    transform={`translate(${cx},${cy}) rotate(45)`}
    onClick={(e) => { e.stopPropagation(); onOpen(marker); }}
    style={{ cursor: "pointer" }}
    data-testid={`event-marker-${marker.source}-${marker.event_id}`}
  >
    <rect x="-6" y="-6" width="12" height="12" fill={color} stroke="#0a0a0a" strokeWidth="1.5" />
    <title>{marker.title}</title>
  </g>
);

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <div className="text-white/50 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="font-mono text-white/90">
            {p.dataKey === "sales_usd" ? `$${Number(p.value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : (p.value || 0).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function TrafficTrendChart({ timeline, salesTimeline }) {
  const [analyticsEvents, setAnalyticsEvents] = useState([]);
  const [affiliateEvents, setAffiliateEvents] = useState([]);
  const [trendLines, setTrendLines] = useState({
    visitors: true, views: true, signups: true, sales: true,
    system_events: true, affiliate_events: true,
  });
  const [affiliateFilter, setAffiliateFilter] = useState(null);

  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formDateTime, setFormDateTime] = useState("");
  const [savingEvent, setSavingEvent] = useState(false);

  const [affiliateModalOpen, setAffiliateModalOpen] = useState(false);
  const [viewingAffiliateEvent, setViewingAffiliateEvent] = useState(null);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliateStatsLoading, setAffiliateStatsLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const [sys, aff] = await Promise.all([
        authAxios.get("/analytics/command/timeline-events"),
        authAxios.get("/analytics/command/affiliate-timeline-events").catch(() => ({ data: { events: [] } })),
      ]);
      setAnalyticsEvents(sys.data?.events || []);
      setAffiliateEvents(aff.data?.events || []);
    } catch (err) { console.error("timeline events fetch failed:", err); }
  }, []);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const openAdd = () => {
    setEditingEvent(null); setFormTitle(""); setFormDesc("");
    setFormDateTime(toLocalInput(new Date().toISOString()));
    setEventModalOpen(true);
  };
  const openViewSystem = (ev) => {
    setEditingEvent(ev); setFormTitle(ev.title || ""); setFormDesc(ev.description || "");
    setFormDateTime(toLocalInput(ev.event_at));
    setEventModalOpen(true);
  };
  const openViewAffiliate = useCallback(async (ev) => {
    setViewingAffiliateEvent(ev); setAffiliateStats(null); setAffiliateModalOpen(true);
    if (!ev?.user_id) return;
    setAffiliateStatsLoading(true);
    try {
      const { data } = await authAxios.get(`/analytics/command/affiliate-network-stats/${ev.user_id}`);
      setAffiliateStats(data);
    } catch (err) { toast.error("Failed to load affiliate stats"); }
    finally { setAffiliateStatsLoading(false); }
  }, []);
  const applyAffFilter = () => {
    if (!viewingAffiliateEvent) return;
    setAffiliateFilter({
      user_id: viewingAffiliateEvent.user_id,
      display_name: viewingAffiliateEvent.affiliate_name || affiliateStats?.display_name || viewingAffiliateEvent.user_id,
    });
    setAffiliateModalOpen(false);
    toast.success(`Filtering by ${viewingAffiliateEvent.affiliate_name || "affiliate"}`);
  };

  const saveEvent = async () => {
    if (!formTitle.trim()) { toast.error("Title is required"); return; }
    setSavingEvent(true);
    try {
      if (editingEvent) {
        await authAxios.put(`/analytics/command/timeline-events/${editingEvent.event_id}`, {
          title: formTitle, description: formDesc, event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event updated");
      } else {
        await authAxios.post(`/analytics/command/timeline-events`, {
          title: formTitle, description: formDesc, event_at: fromLocalInput(formDateTime),
        });
        toast.success("Event added");
      }
      setEventModalOpen(false); fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save event"); }
    finally { setSavingEvent(false); }
  };
  const deleteEvent = async () => {
    if (!editingEvent || !window.confirm(`Delete "${editingEvent.title}"?`)) return;
    setSavingEvent(true);
    try {
      await authAxios.delete(`/analytics/command/timeline-events/${editingEvent.event_id}`);
      toast.success("Event deleted"); setEventModalOpen(false); fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
    finally { setSavingEvent(false); }
  };

  // ── Chart data assembly ──
  const baseTimeline = timeline?.timeline || [];
  const existingDates = new Set(baseTimeline.map((t) => t.date));
  const salesMap = new Map((salesTimeline?.timeline || []).map((s) => [s.date, s.sales_usd]));
  const allEventDates = [
    ...(trendLines.system_events ? analyticsEvents : []),
    ...(trendLines.affiliate_events
      ? (affiliateFilter
          ? affiliateEvents.filter((e) => e.user_id === affiliateFilter.user_id)
          : affiliateEvents)
      : []),
  ].map((ev) => (ev.event_at || "").slice(0, 10)).filter((d) => d && !existingDates.has(d));
  const chartRows = [
    ...baseTimeline.map((r) => ({ ...r, sales_usd: salesMap.get(r.date) || 0 })),
    ...allEventDates.map((d) => ({ date: d, views: 0, visitors: 0, signups: 0, sales_usd: salesMap.get(d) || 0 })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const buildMarkers = (events, source, color) => events.map((ev) => {
    const bucket = (ev.event_at || "").slice(0, 10);
    const row = chartRows.find((t) => t.date === bucket);
    if (!row) return null;
    const candidates = [];
    if (trendLines.visitors) candidates.push(row.visitors || 0);
    if (trendLines.views) candidates.push(row.views || 0);
    if (trendLines.signups) candidates.push(row.signups || 0);
    const y = candidates.length ? Math.max(...candidates) : 0;
    return { ...ev, bucket, y, source, color };
  }).filter(Boolean);

  const filteredAffEvents = affiliateFilter
    ? affiliateEvents.filter((e) => e.user_id === affiliateFilter.user_id)
    : affiliateEvents;
  const systemMarkers = trendLines.system_events ? buildMarkers(analyticsEvents, "system", "#7dd3fc") : [];
  const affiliateMarkers = trendLines.affiliate_events ? buildMarkers(filteredAffEvents, "affiliate", "#c084fc") : [];

  return (
    <>
      <section className="rounded-xl border border-white/[0.06] bg-[#111113] p-5" data-testid="trend-timeline">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white/50 uppercase tracking-wider flex items-center gap-2">
            <Activity className="w-4 h-4" /> Traffic Trend
          </h2>
          <Button
            size="sm"
            onClick={openAdd}
            className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium h-8"
            data-testid="analytics-add-event-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add event
          </Button>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartRows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
            <XAxis dataKey="date" tick={{ fill: "#555", fontSize: 11 }} tickFormatter={d => d.slice(5)} />
            <YAxis yAxisId="left" tick={{ fill: "#555", fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: "#22d3ee", fontSize: 11 }}
              tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(1) + 'k' : Math.round(v)}`} />
            <Tooltip content={<ChartTooltip />} />
            {trendLines.visitors && <Line yAxisId="left" type="monotone" dataKey="visitors" stroke={GOLD} strokeWidth={2} dot={false} name="Visitors" />}
            {trendLines.views && <Line yAxisId="left" type="monotone" dataKey="views" stroke={GOLD_DIM} strokeWidth={1.5} dot={false} name="Page Views" />}
            {trendLines.signups && <Line yAxisId="left" type="monotone" dataKey="signups" stroke="#22c55e" strokeWidth={1.5} dot={false} name="Signups" />}
            {trendLines.sales && <Line yAxisId="right" type="monotone" dataKey="sales_usd" stroke="#22d3ee" strokeWidth={2} dot={false} name="Sales $" />}
            {[...systemMarkers, ...affiliateMarkers].map((m) => (
              <ReferenceDot yAxisId="left" key={`${m.source}_${m.event_id}`} x={m.bucket} y={m.y} r={8} isFront
                // eslint-disable-next-line react/no-unstable-nested-components
                shape={(props) => (
                  <AnalyticsDiamondMarker cx={props.cx} cy={props.cy} marker={m} color={m.color}
                    onOpen={m.source === "system" ? openViewSystem : openViewAffiliate} />
                )}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-5 mt-3 pt-3 border-t border-white/[0.04] flex-wrap">
          {[
            { key: "visitors", label: "Visitors", color: GOLD },
            { key: "views", label: "Page Views", color: GOLD_DIM },
            { key: "signups", label: "Signups", color: "#22c55e" },
            { key: "sales", label: "Sales $", color: "#22d3ee" },
            { key: "system_events", label: "System Events", color: "#7dd3fc" },
            { key: "affiliate_events", label: "Affiliate Events", color: "#c084fc" },
          ].map(l => (
            <label key={l.key} className="flex items-center gap-2 cursor-pointer select-none" data-testid={`trend-toggle-${l.key}`}>
              <input type="checkbox" checked={trendLines[l.key]} onChange={() => setTrendLines(p => ({ ...p, [l.key]: !p[l.key] }))} className="sr-only peer" />
              <span className="w-4 h-4 rounded border-2 flex items-center justify-center transition-all peer-checked:border-transparent"
                style={{ borderColor: trendLines[l.key] ? l.color : "#333", backgroundColor: trendLines[l.key] ? l.color : "transparent" }}>
                {trendLines[l.key] && (
                  <svg className="w-2.5 h-2.5 text-black" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 6l3 3 5-5" /></svg>
                )}
              </span>
              <span className={`text-xs ${trendLines[l.key] ? "text-white/70" : "text-white/25"}`}>{l.label}</span>
            </label>
          ))}
          {affiliateFilter && (
            <button type="button" onClick={() => setAffiliateFilter(null)} data-testid="affiliate-filter-chip"
              className="flex items-center gap-2 ml-auto px-2.5 py-1 rounded-full border border-[#c084fc]/40 bg-[#c084fc]/10 hover:bg-[#c084fc]/20 group">
              <span className="w-4 h-4 rounded flex items-center justify-center" style={{ backgroundColor: "#c084fc" }}>
                <svg className="w-2.5 h-2.5 text-black" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 6l3 3 5-5" /></svg>
              </span>
              <span className="text-xs text-white/80">
                Filtered by Affiliate: <span className="font-medium text-[#c084fc]">{affiliateFilter.display_name}</span>
              </span>
              <span className="text-white/40 group-hover:text-white/80 text-sm">×</span>
            </button>
          )}
        </div>
      </section>

      {/* Add/Edit/View system event modal */}
      <Dialog open={eventModalOpen} onOpenChange={setEventModalOpen}>
        <DialogContent className="bg-[#0a0a0f] border border-divine-gold/25 text-white" data-testid="analytics-event-modal">
          <DialogHeader>
            <DialogTitle className="text-divine-gold flex items-center gap-2">
              <Diamond className="w-4 h-4 text-sky-300 fill-sky-300" />
              {editingEvent ? "Marketing Event" : "Add Marketing Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-white/50">Log any marketing campaign, launch, or promotion — the diamond will land on the Traffic Trend graph.</p>
            <div>
              <label className="text-xs text-white/60 block mb-1">Title</label>
              <Input value={formTitle} onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Instagram launch, TikTok ad, newsletter blast"
                className="bg-white/5 border-white/10" data-testid="analytics-event-title-input" autoFocus />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Date & Time</label>
              <Input type="datetime-local" value={formDateTime} onChange={(e) => setFormDateTime(e.target.value)}
                className="bg-white/5 border-white/10 text-white" data-testid="analytics-event-datetime-input" />
            </div>
            <div>
              <label className="text-xs text-white/60 block mb-1">Description</label>
              <textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} rows={4}
                placeholder="Optional — audience, budget, links, expected impact"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                data-testid="analytics-event-desc-input" />
            </div>
            {editingEvent && (
              <div className="text-[10px] text-white/40">Recorded {new Date(editingEvent.event_at).toLocaleString()}</div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            {editingEvent && (
              <Button variant="outline" onClick={deleteEvent} disabled={savingEvent}
                className="border-red-500/40 text-red-400 hover:bg-red-500/10 mr-auto" data-testid="analytics-event-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            )}
            <Button variant="ghost" onClick={() => setEventModalOpen(false)} className="text-white/60 hover:text-white" data-testid="analytics-event-cancel-btn">Cancel</Button>
            <Button onClick={saveEvent} disabled={savingEvent || !formTitle.trim()}
              className="bg-divine-gold hover:bg-divine-gold/90 text-black font-medium" data-testid="analytics-event-save-btn">
              {savingEvent ? "Saving…" : editingEvent ? "Save changes" : "Add event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View affiliate event modal */}
      <Dialog open={affiliateModalOpen} onOpenChange={setAffiliateModalOpen}>
        <DialogContent className="bg-[#0a0a0f] border border-[#c084fc]/30 text-white max-w-lg" data-testid="affiliate-event-modal">
          <DialogHeader>
            <DialogTitle className="text-[#c084fc] flex items-center gap-2">
              <Diamond className="w-4 h-4 text-[#c084fc] fill-[#c084fc]" /> Affiliate Marketing Event
            </DialogTitle>
          </DialogHeader>
          {viewingAffiliateEvent && (
            <div className="space-y-4 py-2">
              <div><div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Affiliate</div>
                <div className="text-sm text-white/90 font-medium" data-testid="affiliate-event-name">
                  {viewingAffiliateEvent.affiliate_name || affiliateStats?.display_name || viewingAffiliateEvent.user_id}
                </div>
              </div>
              <div><div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Title</div>
                <div className="text-sm text-white/90">{viewingAffiliateEvent.title}</div>
              </div>
              {viewingAffiliateEvent.description && (
                <div><div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Description</div>
                  <div className="text-sm text-white/70 whitespace-pre-wrap">{viewingAffiliateEvent.description}</div>
                </div>
              )}
              <div className="text-[10px] text-white/40">Recorded {new Date(viewingAffiliateEvent.event_at).toLocaleString()}</div>
              <div className="border-t border-white/[0.06] pt-3">
                <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-2">
                  <DollarSign className="w-3 h-3" /> Network Income (Lifetime)
                </div>
                {affiliateStatsLoading && <div className="text-xs text-white/40 italic">Loading network stats…</div>}
                {!affiliateStatsLoading && affiliateStats && (
                  <div className="rounded-lg border border-white/[0.06] overflow-hidden" data-testid="affiliate-network-stats">
                    <table className="w-full text-xs">
                      <thead className="bg-white/[0.03] text-white/50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Tier</th>
                          <th className="text-right px-3 py-2 font-medium"><Network className="w-3 h-3 inline" /></th>
                          <th className="text-right px-3 py-2 font-medium">Cash</th>
                          <th className="text-right px-3 py-2 font-medium">Coins</th>
                          <th className="text-right px-3 py-2 font-medium">#</th>
                        </tr>
                      </thead>
                      <tbody className="text-white/80">
                        {[
                          { role: "affiliate_tier_1", label: "Direct" },
                          { role: "affiliate_tier_2", label: "Sub Affiliates" },
                          { role: "affiliate_tier_3", label: "Deep Affiliates" },
                        ].map((t) => {
                          const row = affiliateStats.by_tier?.[t.role] || { cash_cents: 0, coins: 0, count: 0, network_size: 0 };
                          return (
                            <tr key={t.role} className="border-t border-white/[0.04]">
                              <td className="px-3 py-2">{t.label}</td>
                              <td className="px-3 py-2 text-right font-mono text-white/80">{(row.network_size || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right font-mono">${(row.cash_cents / 100).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono text-[#C5A55A]">{row.coins.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-white/50">{row.count}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {!affiliateStatsLoading && !affiliateStats && <div className="text-xs text-white/40 italic">No network data available.</div>}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button onClick={applyAffFilter}
              disabled={!viewingAffiliateEvent || (affiliateFilter?.user_id === viewingAffiliateEvent?.user_id)}
              className="bg-[#c084fc] hover:bg-[#c084fc]/90 text-black font-medium mr-auto"
              data-testid="affiliate-event-filter-btn">
              {affiliateFilter?.user_id === viewingAffiliateEvent?.user_id ? "Already Filtered" : "Filter By This Affiliate"}
            </Button>
            <Button variant="ghost" onClick={() => setAffiliateModalOpen(false)} className="text-white/60 hover:text-white">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
